import React, { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import {
  Plus,
  Trash2,
  Save,
  ShoppingCart,
  CalendarDays,
  Warehouse,
  User,
  Wallet,
  ReceiptText,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { fetchCompanyBankAccounts } from "./Trade/bankAccountsFetch";
import "../Product/SalesQuickEntry.css";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function round2(n) {
  return Number(Number(n || 0).toFixed(2));
}

function money(n) {
  return `₦${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function SummaryCard({ icon: Icon, label, value, hint, tone = "default" }) {
  return (
    <div className={`sqe-summary-card ${tone}`}>
      <div className="sqe-summary-icon">
        <Icon size={18} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {hint && <small>{hint}</small>}
      </div>
    </div>
  );
}

export default function SalesQuickEntry() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [showTaxAdvanced, setShowTaxAdvanced] = useState(false);

  const [saleDate, setSaleDate] = useState(todayISO());
  const [warehouseId, setWarehouseId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [bankAccountId, setBankAccountId] = useState("");
  const [notes, setNotes] = useState("");

  const [vatExempt, setVatExempt] = useState(true);
  const [whtExempt, setWhtExempt] = useState(true);

  const [vatRate, setVatRate] = useState(7.5);
  const [whtRate, setWhtRate] = useState(0);

  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [lines, setLines] = useState([
    { item_id: "", quantity: "1", unit_price: "0", description: "" },
  ]);

  useEffect(() => {
    (async () => {
      try {
        if (!companyId) return;

        const { data: wh, error: whErr } = await supabase
          .from("warehouses")
          .select("id,name,is_default")
          .eq("company_id", companyId)
          .order("name", { ascending: true });

        if (whErr) throw whErr;
        setWarehouses(wh || []);

        const def = (wh || []).find((w) => w.is_default);
        if (def?.id) setWarehouseId(def.id);

        const { data: cust, error: custErr } = await supabase
          .from("customers")
          .select("id,name,company_name")
          .eq("company_id", companyId)
          .order("name", { ascending: true });

        if (custErr) throw custErr;
        setCustomers(cust || []);

        const { data: it, error: itErr } = await supabase
          .from("inventory_items")
          .select("id,sku,name,unit,item_type,sales_price")
          .eq("company_id", companyId)
          .eq("is_active", true)
          .order("name", { ascending: true });

        if (itErr) throw itErr;
        setItems(it || []);

        const banks = await fetchCompanyBankAccounts(companyId);
        setBankAccounts(banks || []);
      } catch (e) {
        console.error("SalesQuickEntry init error:", e);
        setErr(e?.message || "Failed to load sales quick entry data.");
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (!isPaid) setBankAccountId("");
  }, [isPaid]);

  function setLine(index, patch) {
    setLines((prev) =>
      prev.map((line, idx) => (idx === index ? { ...line, ...patch } : line))
    );
  }

  const applyItemToLine = useCallback(
    (lineIndex, itemId) => {
      const item = (items || []).find((x) => x.id === itemId);

      const price =
        item?.sales_price === null || item?.sales_price === undefined
          ? ""
          : String(Number(item.sales_price || 0));

      setLine(lineIndex, {
        item_id: itemId,
        description: "",
        unit_price: price === "" ? "0" : price,
      });
    },
    [items]
  );

  function addLine() {
    setLines((prev) => [
      ...prev,
      { item_id: "", quantity: "1", unit_price: "0", description: "" },
    ]);
  }

  function removeLine(index) {
    setLines((prev) => prev.filter((_, idx) => idx !== index));
  }

  const subtotal = useMemo(() => {
    return round2(
      lines.reduce((sum, line) => {
        const qty = Number(line.quantity || 0);
        const price = Number(line.unit_price || 0);
        return sum + (qty > 0 ? qty * price : 0);
      }, 0)
    );
  }, [lines]);

  const taxSummary = useMemo(() => {
    const activeVatRate = vatExempt ? 0 : Number(vatRate || 0);
    const activeWhtRate = whtExempt ? 0 : Number(whtRate || 0);

    const vat = round2(subtotal * (activeVatRate / 100));
    const gross = round2(subtotal + vat);
    const wht = round2(subtotal * (activeWhtRate / 100));
    const net = round2(gross - wht);

    return {
      subtotal,
      vatRate: activeVatRate,
      vatAmount: vat,
      grossTotal: gross,
      whtRate: activeWhtRate,
      whtAmount: wht,
      netAmount: net,
    };
  }, [subtotal, vatRate, whtRate, vatExempt, whtExempt]);

  async function postSale() {
    setErr("");

    if (!companyId) return setErr("Missing companyId.");
    if (!warehouseId)
      return setErr(
        "Please select the warehouse where the sale should be recorded."
      );
    if (isPaid && !bankAccountId) {
      return setErr(
        "Please select the bank account that received the payment."
      );
    }

    const cleanLines = lines
      .map((line) => ({
        item_id: line.item_id || null,
        quantity: Number(line.quantity || 0),
        unit_price: Number(line.unit_price || 0),
        description: line.description?.trim() || null,
      }))
      .filter(
        (line) => line.quantity > 0 && (line.item_id || line.description)
      );

    if (!cleanLines.length) {
      return setErr("Add at least one sale line before posting.");
    }

    setBusy(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) throw new Error("Not authenticated.");

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/sales-post`;

      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          sale_date: saleDate,
          customer_id: customerId || null,
          payment_status: isPaid ? "PAID" : "UNPAID",
          bank_account_id: isPaid ? bankAccountId : null,
          warehouse_id: warehouseId,
          notes: notes?.trim() || null,
          lines: cleanLines,
          total_amount: Number(subtotal || 0),

          vat_rate: Number(taxSummary.vatRate || 0) / 100,
          vat_amount: taxSummary.vatAmount,
          wht_rate: Number(taxSummary.whtRate || 0) / 100,
          wht_amount: taxSummary.whtAmount,
          gross_total: taxSummary.grossTotal,
          net_amount: taxSummary.netAmount,
          tax_json: {
            subtotal: taxSummary.subtotal,
            vat_exempt: vatExempt,
            vat_rate: Number(taxSummary.vatRate || 0) / 100,
            vat_amount: taxSummary.vatAmount,
            wht_exempt: whtExempt,
            wht_rate: Number(taxSummary.whtRate || 0) / 100,
            wht_amount: taxSummary.whtAmount,
            gross_total: taxSummary.grossTotal,
            net_amount: taxSummary.netAmount,
          },
        }),
      });

      const raw = await res.text();
      let json = {};

      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw || "Unknown error" };
      }

      if (!res.ok) {
        console.error("sales-post response:", {
          status: res.status,
          raw,
          json,
        });

        throw new Error(
          json?.error ||
            json?.details ||
            raw ||
            `Failed to post sale (HTTP ${res.status})`
        );
      }

      alert(
        `Sale posted successfully. Gross total: ${money(taxSummary.grossTotal)}`
      );

      setNotes("");
      setCustomerId("");
      setIsPaid(false);
      setBankAccountId("");
      setVatRate(7.5);
      setWhtRate(0);
      setVatExempt(true);
      setWhtExempt(true);
      setLines([
        { item_id: "", quantity: "1", unit_price: "0", description: "" },
      ]);
    } catch (e) {
      setErr(e.message || "Error posting sale.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="salesqe-page">
      <section className="sqe-hero">
        <div>
          <p className="sqe-eyebrow">Sales</p>
          <h1>Quick Sale Entry</h1>
          <p>
            Record a sale quickly, update inventory, post accounting journals,
            and track whether payment has been received.
          </p>
        </div>

        <div className="sqe-hero-total">
          <span>Gross Total</span>
          <strong>{money(taxSummary.grossTotal)}</strong>
        </div>
      </section>

      {err && (
        <div className="sqe-banner error">
          <AlertCircle size={17} />
          {err}
        </div>
      )}

      <section className="sqe-summary-grid">
        <SummaryCard
          icon={ReceiptText}
          label="Subtotal"
          value={money(taxSummary.subtotal)}
          hint="Before VAT and WHT"
        />
        <SummaryCard
          icon={ShoppingCart}
          label="VAT"
          value={money(taxSummary.vatAmount)}
          hint={vatExempt ? "VAT exempt" : `${taxSummary.vatRate}% applied`}
          tone={vatExempt ? "default" : "warning"}
        />
        <SummaryCard
          icon={Wallet}
          label="Net Receivable"
          value={money(taxSummary.netAmount)}
          hint="Expected amount after WHT"
          tone="success"
        />
        <SummaryCard
          icon={AlertCircle}
          label="Payment Status"
          value={isPaid ? "Paid" : "Unpaid"}
          hint={isPaid ? "Payment will hit bank" : "Customer receivable"}
          tone={isPaid ? "success" : "warning"}
        />
      </section>

      <section className="sqe-panel">
        <div className="sqe-section-head">
          <div>
            <h3>Sale Details</h3>
            <p>Choose the sale date, warehouse, customer and payment status.</p>
          </div>
        </div>

        <div className="sqe-form-grid">
          <label className="sqe-field">
            <span>
              <CalendarDays size={15} />
              Sale Date
            </span>
            <input
              className="sqe-input"
              type="date"
              value={saleDate}
              onChange={(e) => setSaleDate(e.target.value)}
              disabled={busy}
            />
          </label>

          <label className="sqe-field">
            <span>
              <Warehouse size={15} />
              Warehouse
            </span>
            <select
              className="sqe-input"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              disabled={busy}
            >
              <option value="">Select warehouse…</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                  {warehouse.is_default ? " (Default)" : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="sqe-field">
            <span>
              <User size={15} />
              Customer
            </span>
            <select
              className="sqe-input"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              disabled={busy}
            >
              <option value="">Walk-in customer / optional…</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                  {customer.company_name ? ` - ${customer.company_name}` : ""}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="sqe-payment-box">
          <div>
            <strong>Payment received?</strong>
            <p>
              Mark as paid only when money has been received. If unpaid, the
              sale remains a customer receivable.
            </p>
          </div>

          <label className="sqe-switch">
            <input
              type="checkbox"
              checked={isPaid}
              onChange={(e) => setIsPaid(e.target.checked)}
              disabled={busy}
            />
            <span>{isPaid ? "Paid" : "Unpaid"}</span>
          </label>
        </div>

        {isPaid && (
          <label className="sqe-field sqe-bank-field">
            <span>
              <Wallet size={15} />
              Receiving Bank Account
            </span>
            <select
              className="sqe-input"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              disabled={busy}
            >
              <option value="">Select bank account…</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.account_number ? `${account.account_number} - ` : ""}
                  {account.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </section>

      <section className="sqe-panel">
        <div className="sqe-section-head">
          <div>
            <h3>Tax Setup</h3>
            <p>
              VAT increases the invoice value. WHT reduces the expected cash
              receivable.
            </p>
          </div>

          <button
            className="sqe-btn ghost"
            type="button"
            onClick={() => setShowTaxAdvanced((prev) => !prev)}
            disabled={busy}
          >
            {showTaxAdvanced ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
            {showTaxAdvanced ? "Hide Details" : "Show Details"}
          </button>
        </div>

        <div className="sqe-tax-toggle-grid">
          <label className="sqe-tax-toggle">
            <input
              type="checkbox"
              checked={vatExempt}
              onChange={(e) => setVatExempt(e.target.checked)}
              disabled={busy}
            />
            <div>
              <strong>VAT Exempt</strong>
              <span>
                {vatExempt ? "VAT will not be added" : "VAT will be applied"}
              </span>
            </div>
          </label>

          <label className="sqe-tax-toggle">
            <input
              type="checkbox"
              checked={whtExempt}
              onChange={(e) => setWhtExempt(e.target.checked)}
              disabled={busy}
            />
            <div>
              <strong>WHT Exempt</strong>
              <span>
                {whtExempt ? "No WHT deduction" : "WHT will be deducted"}
              </span>
            </div>
          </label>
        </div>

        {showTaxAdvanced && (
          <div className="sqe-form-grid sqe-tax-grid">
            <label className="sqe-field">
              <span>VAT Rate %</span>
              <input
                className="sqe-input"
                type="number"
                min="0"
                step="0.01"
                value={vatRate}
                onChange={(e) => setVatRate(e.target.value)}
                disabled={busy || vatExempt}
              />
            </label>

            <label className="sqe-field">
              <span>WHT Rate %</span>
              <input
                className="sqe-input"
                type="number"
                min="0"
                step="0.01"
                value={whtRate}
                onChange={(e) => setWhtRate(e.target.value)}
                disabled={busy || whtExempt}
              />
            </label>

            <label className="sqe-field">
              <span>VAT Amount</span>
              <input
                className="sqe-input"
                value={money(taxSummary.vatAmount)}
                readOnly
              />
            </label>

            <label className="sqe-field">
              <span>WHT Amount</span>
              <input
                className="sqe-input"
                value={money(taxSummary.whtAmount)}
                readOnly
              />
            </label>
          </div>
        )}
      </section>

      <section className="sqe-panel">
        <div className="sqe-section-head">
          <div>
            <h3>Sale Lines</h3>
            <p>
              Select inventory items or enter a custom description for
              service/manual sale lines.
            </p>
          </div>

          <button className="sqe-btn ghost" onClick={addLine} disabled={busy}>
            <Plus size={16} />
            Add Line
          </button>
        </div>

        <div className="sqe-lines">
          <div className="sqe-line-header">
            <span>Item / Service</span>
            <span>Description</span>
            <span>Qty</span>
            <span>Unit Price</span>
            <span>Line Total</span>
            <span></span>
          </div>

          {lines.map((line, index) => {
            const lineTotal =
              Number(line.quantity || 0) * Number(line.unit_price || 0);

            return (
              <div key={index} className="sqe-line-row">
                <select
                  className="sqe-input"
                  value={line.item_id}
                  onChange={(e) => applyItemToLine(index, e.target.value)}
                  disabled={busy}
                >
                  <option value="">Manual / service line…</option>
                  {items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.sku} - {item.name} ({item.unit})
                    </option>
                  ))}
                </select>

                <input
                  className="sqe-input"
                  placeholder={
                    line.item_id ? "Auto from selected item" : "Description"
                  }
                  value={line.description}
                  onChange={(e) =>
                    setLine(index, { description: e.target.value })
                  }
                  disabled={busy || !!line.item_id}
                />

                <input
                  className="sqe-input"
                  type="number"
                  placeholder="Qty"
                  value={line.quantity}
                  onChange={(e) => setLine(index, { quantity: e.target.value })}
                  disabled={busy}
                />

                <input
                  className="sqe-input"
                  type="number"
                  placeholder="Unit price"
                  value={line.unit_price}
                  onChange={(e) =>
                    setLine(index, { unit_price: e.target.value })
                  }
                  disabled={busy}
                />

                <div className="sqe-line-total">{money(lineTotal)}</div>

                <button
                  className="sqe-icon-btn danger"
                  onClick={() => removeLine(index)}
                  disabled={busy || lines.length === 1}
                  title="Remove line"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
        </div>

        <label className="sqe-field sqe-notes">
          <span>Notes</span>
          <textarea
            className="sqe-input"
            rows={3}
            placeholder="Optional note for this sale..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
          />
        </label>
      </section>

      <section className="sqe-post-bar">
        <div>
          <span>Amount to Post</span>
          <strong>{money(taxSummary.grossTotal)}</strong>
          <small>Net receivable: {money(taxSummary.netAmount)}</small>
        </div>

        <button className="sqe-btn primary" onClick={postSale} disabled={busy}>
          <Save size={16} />
          {busy ? "Posting Sale…" : "Post Sale"}
        </button>
      </section>
    </div>
  );
}
